import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import './rooms.css';
import SEO from '../../components/SEO';
import Navbar from '../../component/Navbar/Navbar';
import Footer from '../../component/Footer/Footer';
import { api } from '../../api/client';
import { FiMapPin, FiHome, FiChevronRight, FiChevronLeft as FiPrev, FiChevronRight as FiNext } from 'react-icons/fi';
import { ROOMS_INTRO } from '../../constants/brand';

function normalizeAmenities(value) {
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === 'string' ? item : item?.name || ''))
      .map((item) => String(item).trim())
      .filter(Boolean);
  }

  if (typeof value === 'string') {
    const text = value.trim();
    if (!text) return [];

    // JSON array payload (e.g. '["Smart TV","AC"]')
    if (text.startsWith('[') && text.endsWith(']')) {
      try {
        const parsed = JSON.parse(text);
        if (Array.isArray(parsed)) {
          return parsed.map((item) => String(item).trim()).filter(Boolean);
        }
      } catch {
        // fall through to comma/newline split
      }
    }

    // Fallback for comma-separated or line-separated text.
    return text
      .split(/,|\r?\n/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
}

/** OTA-style nightly price: strikethrough reference + discounted rate + label when API sends promotion or stay discount. */
function apartmentCardPricing(apt) {
  const stayDisc = apt.price_was != null && apt.discount_label && apt.price != null;
  if (stayDisc) {
    return (
      <div className="rooms-apt-price-stack">
        <span className="rooms-apt-discount-badge">{apt.discount_label}</span>
        <div className="rooms-apt-price-inline" aria-label="Nightly price after discount">
          <span className="rooms-apt-price-was">₦{Number(apt.price_was).toLocaleString()}</span>
          <span className="rooms-apt-price-sep" aria-hidden>
            →
          </span>
          <span className="rooms-apt-card-price">
            ₦{Number(apt.price).toLocaleString()}
            <span className="rooms-apt-card-per"> / night</span>
          </span>
        </div>
      </div>
    );
  }
  const promo = apt.price_promotion;
  if (
    promo &&
    promo.per_night_before != null &&
    promo.per_night_after != null &&
    Number(promo.per_night_after) < Number(promo.per_night_before)
  ) {
    return (
      <div className="rooms-apt-price-stack">
        {promo.label ? <span className="rooms-apt-discount-badge">{promo.label}</span> : null}
        <div className="rooms-apt-price-inline" aria-label="Promotional nightly rate">
          <span className="rooms-apt-price-was">₦{Number(promo.per_night_before).toLocaleString()}</span>
          <span className="rooms-apt-price-sep" aria-hidden>
            →
          </span>
          <span className="rooms-apt-card-price">
            ₦{Number(promo.per_night_after).toLocaleString()}
            <span className="rooms-apt-card-per"> / night</span>
          </span>
        </div>
      </div>
    );
  }
  if (apt.price != null) {
    return (
      <span className="rooms-apt-card-price">
        ₦{Number(apt.price).toLocaleString()}
        <span className="rooms-apt-card-per"> / night</span>
      </span>
    );
  }
  return <span className="rooms-apt-card-price rooms-apt-card-per">Price on request</span>;
}

/** Build query string for Rooms search / URL sync (pure). */
function filtersDimensionsKey(f) {
  return JSON.stringify({
    city: f.city,
    start_date: f.start_date,
    end_date: f.end_date,
    adults: f.adults,
    children: f.children,
    price_from: f.price_from,
    price_to: f.price_to,
    sort: f.sort,
  });
}

function buildRoomsSearchParams(inputFilters, includeDefaultSort = false) {
  const params = new URLSearchParams();
  if (inputFilters.city) params.set('city', inputFilters.city);
  if (inputFilters.start_date) params.set('start_date', inputFilters.start_date);
  if (inputFilters.end_date) params.set('end_date', inputFilters.end_date);
  if (inputFilters.adults) params.set('adults', inputFilters.adults);
  if (inputFilters.children !== '') params.set('children', inputFilters.children);
  if (inputFilters.price_from) params.set('price_from', inputFilters.price_from);
  if (inputFilters.price_to) params.set('price_to', inputFilters.price_to);
  if (inputFilters.sort && (includeDefaultSort || inputFilters.sort !== 'rating_desc')) {
    params.set('sort', inputFilters.sort);
  }
  return params;
}

const Rooms = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState(null);
  const [copyStatus, setCopyStatus] = useState('');
  const [pagination, setPagination] = useState(null);
  const [filters, setFilters] = useState({
    city: searchParams.get('city') || '',
    start_date: searchParams.get('start_date') || '',
    end_date: searchParams.get('end_date') || '',
    adults: searchParams.get('adults') || '',
    children: searchParams.get('children') || '',
    price_from: searchParams.get('price_from') || '',
    price_to: searchParams.get('price_to') || '',
    sort: searchParams.get('sort') || 'rating_desc',
  });

  const apartmentDetailPath = useMemo(() => {
    if (filters.start_date && filters.end_date) {
      const q = new URLSearchParams({
        start_date: filters.start_date,
        end_date: filters.end_date,
      });
      return (aptId) => `/apartments/${aptId}?${q.toString()}`;
    }
    return (aptId) => `/apartments/${aptId}`;
  }, [filters.start_date, filters.end_date]);

  const hasActiveFilters = useMemo(
    () => Boolean(
      filters.city
      || filters.start_date
      || filters.end_date
      || filters.adults
      || filters.children
      || filters.price_from
      || filters.price_to
      || filters.sort !== 'rating_desc'
    ),
    [filters],
  );

  const activeFilterChips = useMemo(() => {
    const chips = [];
    const cityName = cities.find((c) => String(c.id) === String(filters.city))?.name;
    if (filters.city) chips.push({ key: 'city', label: `City: ${cityName || filters.city}` });
    if (filters.start_date) chips.push({ key: 'start_date', label: `Check-in: ${filters.start_date}` });
    if (filters.end_date) chips.push({ key: 'end_date', label: `Check-out: ${filters.end_date}` });
    if (filters.adults) chips.push({ key: 'adults', label: `Adults: ${filters.adults}` });
    if (filters.children !== '') chips.push({ key: 'children', label: `Children: ${filters.children}` });
    if (filters.price_from) chips.push({ key: 'price_from', label: `Min ₦${Number(filters.price_from).toLocaleString()}` });
    if (filters.price_to) chips.push({ key: 'price_to', label: `Max ₦${Number(filters.price_to).toLocaleString()}` });
    if (filters.sort !== 'rating_desc') {
      const sortLabels = {
        price_asc: 'Sort: Price low-high',
        price_desc: 'Sort: Price high-low',
      };
      chips.push({ key: 'sort', label: sortLabels[filters.sort] || `Sort: ${filters.sort}` });
    }
    return chips;
  }, [filters, cities]);

  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  /** Tracks filter dimensions so we only strip `page` from the URL when those change (not on pagination alone). */
  const filtersDimensionsSerializedRef = useRef(null);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedFilters(filters), 250);
    return () => window.clearTimeout(t);
  }, [filters]);

  useEffect(() => {
    let cancelled = false;
    api
      .get('cities', null)
      .then((res) => {
        if (cancelled) return;
        const list = res?.data ?? [];
        setCities(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setCities([]);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const next = {
      city: searchParams.get('city') || '',
      start_date: searchParams.get('start_date') || '',
      end_date: searchParams.get('end_date') || '',
      adults: searchParams.get('adults') || '',
      children: searchParams.get('children') || '',
      price_from: searchParams.get('price_from') || '',
      price_to: searchParams.get('price_to') || '',
      sort: searchParams.get('sort') || 'rating_desc',
    };
    setFilters((prev) => {
      const changed = Object.keys(next).some((key) => prev[key] !== next[key]);
      return changed ? next : prev;
    });
  }, [searchParams]);

  useEffect(() => {
    const serialized = filtersDimensionsKey(filters);
    const prev = filtersDimensionsSerializedRef.current;
    const filterDimensionsChanged = prev !== null && prev !== serialized;
    filtersDimensionsSerializedRef.current = serialized;

    const params = buildRoomsSearchParams(filters, false);
    if (!filterDimensionsChanged) {
      const p = parseInt(searchParams.get('page') || '1', 10);
      if (!Number.isNaN(p) && p > 1) {
        params.set('page', String(p));
      }
    }
    const current = searchParams.toString();
    const next = params.toString();
    if (current !== next) {
      setSearchParams(params, { replace: true });
    }
  }, [filters, searchParams, setSearchParams]);

  const loadProperties = useCallback((page = 1) => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params = buildRoomsSearchParams(debouncedFilters, true);
    if (page > 1) params.set('page', page);
    const query = params.toString();
    api
      .get(`search${query ? `?${query}` : ''}`, null)
      .then((res) => {
        if (cancelled) return;
        const list = res?.properties?.data ?? res?.properties ?? [];
        setProperties(Array.isArray(list) ? list : []);
        const meta = res?.properties?.meta ?? null;
        setPagination(meta);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message || 'Could not load properties');
          setProperties([]);
          setPagination(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setHasLoadedOnce(true);
        }
      });
    return () => { cancelled = true; };
  }, [debouncedFilters]);

  useEffect(() => {
    const page = parseInt(searchParams.get('page'), 10) || 1;
    const cleanup = loadProperties(page);
    return cleanup;
  }, [loadProperties, searchParams]);

  const goToPage = (page) => {
    const params = buildRoomsSearchParams(filters, false);
    if (page > 1) params.set('page', String(page));
    setSearchParams(params, { replace: false });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => {
      const next = { ...prev, [name]: value };
      // Keep date range valid while typing.
      if (name === 'start_date' && next.end_date && next.end_date <= value) {
        next.end_date = '';
      }
      return next;
    });
  };

  const clearFilters = () => {
    setFilters({
      city: '',
      start_date: '',
      end_date: '',
      adults: '',
      children: '',
      price_from: '',
      price_to: '',
      sort: 'rating_desc',
    });
  };

  const removeFilterChip = (key) => {
    setFilters((prev) => ({
      ...prev,
      [key]: key === 'sort' ? 'rating_desc' : '',
    }));
  };

  const handleCopyFilterLink = async () => {
    try {
      const url = window.location.href;
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const temp = document.createElement('textarea');
        temp.value = url;
        temp.setAttribute('readonly', '');
        temp.style.position = 'absolute';
        temp.style.left = '-9999px';
        document.body.appendChild(temp);
        temp.select();
        document.execCommand('copy');
        document.body.removeChild(temp);
      }
      setCopyStatus('Copied!');
    } catch {
      setCopyStatus('Copy failed');
    } finally {
      window.setTimeout(() => setCopyStatus(''), 1600);
    }
  };

  const hasApiData = properties.length > 0;
  const totalApartments = useMemo(
    () => properties.reduce((sum, property) => sum + (property.apartments?.length || 0), 0),
    [properties],
  );

  return (
    <div className="rooms-page">
      <SEO
        title="Browse Apartments"
        description="Search and book affordable short stays on Luxistt — Abuja, Enugu, Lagos and more. Filter by city, dates, guests and budget."
        path="/rooms"
      />
      <Navbar />
      <div className="rooms-content">
        <h1 className="rooms-page-title">Apartments</h1>
        <p className="rooms-page-intro">
          {ROOMS_INTRO}
        </p>

        <section className="rooms-filters glass-panel">
          <div className="rooms-filter-field">
            <label htmlFor="city">City</label>
            <select id="city" name="city" value={filters.city} onChange={handleFilterChange}>
              <option value="">All cities</option>
              {cities.map((city) => (
                <option key={city.id} value={city.id}>{city.name}</option>
              ))}
            </select>
          </div>
          <div className="rooms-filter-field">
            <label htmlFor="start_date">Check-in</label>
            <input
              id="start_date"
              name="start_date"
              type="date"
              value={filters.start_date}
              onChange={handleFilterChange}
              min={new Date().toISOString().slice(0, 10)}
            />
          </div>
          <div className="rooms-filter-field">
            <label htmlFor="end_date">Check-out</label>
            <input
              id="end_date"
              name="end_date"
              type="date"
              value={filters.end_date}
              onChange={handleFilterChange}
              min={filters.start_date || new Date().toISOString().slice(0, 10)}
            />
          </div>
          <div className="rooms-filter-field">
            <label htmlFor="adults">Adults</label>
            <input
              id="adults"
              name="adults"
              type="number"
              min={1}
              value={filters.adults}
              onChange={handleFilterChange}
              placeholder="e.g. 2"
            />
          </div>
          <div className="rooms-filter-field">
            <label htmlFor="children">Children</label>
            <input
              id="children"
              name="children"
              type="number"
              min={0}
              value={filters.children}
              onChange={handleFilterChange}
              placeholder="e.g. 0"
            />
          </div>
          <div className="rooms-filter-field">
            <label htmlFor="sort">Sort by</label>
            <select id="sort" name="sort" value={filters.sort} onChange={handleFilterChange}>
              <option value="rating_desc">Top rated</option>
              <option value="price_asc">Price: Low to high</option>
              <option value="price_desc">Price: High to low</option>
            </select>
          </div>
          <div className="rooms-filter-field">
            <label htmlFor="price_from">Min price / night (₦)</label>
            <input
              id="price_from"
              name="price_from"
              type="number"
              min={0}
              value={filters.price_from}
              onChange={handleFilterChange}
              placeholder="e.g. 30000"
            />
          </div>
          <div className="rooms-filter-field">
            <label htmlFor="price_to">Max price / night (₦)</label>
            <input
              id="price_to"
              name="price_to"
              type="number"
              min={0}
              value={filters.price_to}
              onChange={handleFilterChange}
              placeholder="e.g. 80000"
            />
          </div>
          <div className="rooms-filter-actions">
            <button
              type="button"
              className="rooms-clear-filters"
              onClick={clearFilters}
              disabled={!hasActiveFilters}
            >
              Clear Filters
            </button>
            <button
              type="button"
              className="rooms-copy-link"
              onClick={handleCopyFilterLink}
            >
              Copy Filter Link
            </button>
            {copyStatus && <span className="rooms-copy-status">{copyStatus}</span>}
          </div>
        </section>
        {activeFilterChips.length > 0 && (
          <div className="rooms-active-filters">
            {activeFilterChips.map((chip) => (
              <button
                key={chip.key}
                type="button"
                className="rooms-filter-chip"
                onClick={() => removeFilterChip(chip.key)}
                aria-label={`Remove ${chip.label}`}
                title="Remove this filter"
              >
                {chip.label} <span aria-hidden>×</span>
              </button>
            ))}
          </div>
        )}

        {loading && !hasLoadedOnce && (
          <div className="rooms-skeleton-list" aria-busy="true" aria-live="polite">
            {[1, 2].map((idx) => (
              <div key={idx} className="rooms-skeleton-card">
                <div className="rooms-skeleton-cover shimmer" />
                <div className="rooms-skeleton-body">
                  <div className="rooms-skeleton-line rooms-skeleton-line-lg shimmer" />
                  <div className="rooms-skeleton-line rooms-skeleton-line-md shimmer" />
                  <div className="rooms-skeleton-line rooms-skeleton-line-sm shimmer" />
                </div>
              </div>
            ))}
          </div>
        )}

        {error && !hasApiData && (
          <div className="rooms-error" role="alert">{error}</div>
        )}

        {hasApiData && (
          <p className="rooms-results-summary">
            Showing <strong>{properties.length}</strong> propert{properties.length === 1 ? 'y' : 'ies'} ·{' '}
            <strong>{totalApartments}</strong> apartment{totalApartments === 1 ? '' : 's'}
            {loading && hasLoadedOnce && <span className="rooms-results-updating"> · Updating…</span>}
          </p>
        )}

        {hasApiData && (
          <div className={`rooms-by-property${loading && hasLoadedOnce ? ' rooms-by-property-loading' : ''}`}>
            {properties.map((property) => {
              const apartments = property.apartments ?? [];

              return (
                <section key={property.id} className="rooms-property-section" id={`property-${property.id}`}>
                  <div className="rooms-property-head">
                    <h2 className="rooms-property-title">{property.name}</h2>
                    {property.address && (
                      <p className="rooms-property-address"><FiMapPin /> {property.address}</p>
                    )}
                    <p className="rooms-property-apartment-count">
                      <FiHome /> {apartments.length} apartment{apartments.length !== 1 ? 's' : ''}
                    </p>
                    {property.description && String(property.description).trim() && (
                      <p className="rooms-property-description">{String(property.description).trim()}</p>
                    )}
                  </div>

                  {/* Apartments grid under property */}
                  {apartments.length > 0 && (
                    <div className="rooms-apt-zone">
                      <div className="rooms-apt-grid">
                        {apartments.map((apt) => {
                          const aptGallery = apt.gallery ?? apt.media ?? [];
                          const firstImage = aptGallery.find((g) => (g.type || 'image') === 'image');
                          const thumbUrl = firstImage?.thumbnail || firstImage?.url || firstImage?.original_url || firstImage?.preview_url || null;
                          const amenities = normalizeAmenities(apt.amenities);

                          return (
                            <Link key={apt.id} to={apartmentDetailPath(apt.id)} className="rooms-apt-card">
                              <div className="rooms-apt-card-img">
                                {thumbUrl ? (
                                  <img src={thumbUrl} alt={apt.name} loading="lazy" decoding="async" />
                                ) : (
                                  <div className="rooms-apt-card-img-ph">
                                    <FiHome />
                                  </div>
                                )}
                                {apt.type && (
                                  <span className="rooms-apt-card-type">{apt.type}</span>
                                )}
                              </div>
                              <div className="rooms-apt-card-body">
                                <h4 className="rooms-apt-card-name">{apt.name}</h4>
                                <div className="rooms-apt-card-specs">
                                  {(apt.capacity_adults != null || apt.capacity_children != null) && (
                                    <span className="rooms-apt-spec rooms-apt-spec-capacity" title="Maximum guests">
                                      {apt.capacity_adults != null
                                        ? `${apt.capacity_adults} adult${Number(apt.capacity_adults) === 1 ? '' : 's'} max`
                                        : ''}
                                      {apt.capacity_adults != null && apt.capacity_children != null ? ' · ' : ''}
                                      {apt.capacity_children != null
                                        ? `${apt.capacity_children} child${Number(apt.capacity_children) === 1 ? '' : 'ren'} max`
                                        : ''}
                                    </span>
                                  )}
                                  {apt.size && <span className="rooms-apt-spec">{apt.size} m²</span>}
                                  {apt.bathrooms != null && (
                                    <span className="rooms-apt-spec">{apt.bathrooms} bath</span>
                                  )}
                                  {apt.room_count != null && apt.room_count > 0 && (
                                    <span className="rooms-apt-spec">{apt.room_count} room{apt.room_count !== 1 ? 's' : ''}</span>
                                  )}
                                  {apt.beds_list && (
                                    <span className="rooms-apt-spec">{apt.beds_list}</span>
                                  )}
                                </div>
                                {apt.description && (
                                  <p className="rooms-apt-card-desc">
                                    {String(apt.description).trim()}
                                  </p>
                                )}
                                {amenities.length > 0 && (
                                  <div className="rooms-apt-card-amenities">
                                    {amenities.map((amenity) => (
                                      <span key={`${apt.id}-${amenity}`} className="rooms-apt-amenity">
                                        {amenity}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                <div className="rooms-apt-card-foot">
                                  {apartmentCardPricing(apt)}
                                  <span className="rooms-apt-card-cta">
                                    View details <FiChevronRight />
                                  </span>
                                </div>
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {pagination && pagination.last_page > 1 && (
          <nav className="rooms-pagination" aria-label="Search results pages">
            <button
              type="button"
              className="rooms-page-btn rooms-page-prev"
              disabled={pagination.current_page <= 1}
              onClick={() => goToPage(pagination.current_page - 1)}
              aria-label="Previous page"
            >
              <FiPrev /> Prev
            </button>
            <div className="rooms-page-numbers">
              {Array.from({ length: pagination.last_page }, (_, i) => i + 1)
                .filter((p) => {
                  const cur = pagination.current_page;
                  return p === 1 || p === pagination.last_page || (p >= cur - 1 && p <= cur + 1);
                })
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                  acc.push(p);
                  return acc;
                }, [])
                .map((item, idx) =>
                  item === '...' ? (
                    <span key={`dots-${idx}`} className="rooms-page-dots">...</span>
                  ) : (
                    <button
                      key={item}
                      type="button"
                      className={`rooms-page-num${item === pagination.current_page ? ' rooms-page-num-active' : ''}`}
                      onClick={() => goToPage(item)}
                      aria-current={item === pagination.current_page ? 'page' : undefined}
                    >
                      {item}
                    </button>
                  ),
                )}
            </div>
            <button
              type="button"
              className="rooms-page-btn rooms-page-next"
              disabled={pagination.current_page >= pagination.last_page}
              onClick={() => goToPage(pagination.current_page + 1)}
              aria-label="Next page"
            >
              Next <FiNext />
            </button>
          </nav>
        )}

        {!loading && !hasApiData && !error && (
          <div className="rooms-empty">
            <p>
              {hasActiveFilters
                ? 'No apartments match your current filters. Try adjusting dates, guest count, city, or price range.'
                : <>No properties available at the moment. Check back later or <Link to="/contact-us">contact us</Link>.</>}
            </p>
          </div>
        )}

        {!loading && hasApiData && (
          <p className="rooms-page-footer">
            Can't find what you need? <Link to="/contact-us">Contact us</Link>.
          </p>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Rooms;
